import { CaseSummarizerResponse } from '@bybeam/intelligence-client/types';
import Admin from './admin.js';
import Application, { VerificationStatus } from './application.js';
import Assignment from './assignment.js';
import {
  CaseParticipant,
  CaseParticipantLinkStatusDetails,
  CaseParticipantStatus,
} from './caseParticipant.js';
import { CaseTag } from './caseTag.js';
import { Comment } from './comment.js';
import Document from './document.js';
import { EligibilityStatus } from './eligibility.js';
import { Fulfillment } from './fulfillment.js';
import Note from './notes.js';
import { PaymentInput, PaymentStatus } from './payment.js';
import Program from './program.js';
import WorkflowEvent from './workflowEvent.js';

export const NoAssignee = 'none';
export const NoTag = 'none';

export enum CaseStatus {
  InProgress = 'InProgress',
  Incomplete = 'Incomplete',
  ReadyForReview = 'ReadyForReview',
  InReview = 'InReview',
  PendingCertification = 'PendingCertification',
  FiscalReview = 'FiscalReview',
  PaymentSent = 'PaymentSent',
  Denied = 'Denied',
  Withdrawn = 'Withdrawn',
  Archived = 'Archived',
  Approved = 'Approved',
}

export interface CaseMetadata {
  id: string;
  caseId: string;
  denialReason?: string;
  deniedAt?: Date;
  withdrawalReason?: string;
  intakeStaff?: string;
  withdrawnAt?: Date;
  incompleteReason?: string;
  incompleteAt?: Date;
}

export enum Priority {
  Default = 10,
  NewApplicant = 3,
  Expedited = 1,
  ExpeditedNewApplicant = 0,
}

export const EXPEDITED_PRIORITIES = new Map([
  [Priority.Expedited, Priority.Default],
  [Priority.ExpeditedNewApplicant, Priority.NewApplicant],
]);

export interface Case {
  id: string;
  displayId: string;
  programId: string;
  program: Program;
  name: string;
  status: CaseStatus;
  metadata: CaseMetadata;
  createdAt: Date;
  deactivatedAt?: Date;
  decisionReachedAt?: Date;
  priority: Priority;
  assignment?: Assignment;
  assignee?: Admin;
  notes?: Note[];
  applications?: Application[];
  documents: Document[];
  fulfillments?: Fulfillment[];
  legacyId?: string;
  statusUpdatedAt?: Date;
  workflowEvents?: WorkflowEvent[];
  participants?: CaseParticipant[];
  participantLinkStatus?: CaseParticipantLinkStatusDetails;
  caseTags?: CaseTag[];
  comments?: Comment[];
  caseSummary?: CaseSummarizerResponse;
}
export default Case;

export interface StatusFilters {
  archived?: boolean;
  expedited?: boolean;
  newApplicant?: boolean;
  referred?: boolean;
  applicantTypeId?: string[];
  caseParticipantStatus?: CaseParticipantStatus[];
  verified?: VerificationStatus[];
  tags?: string[];
  documentTagTypes?: string[];
  answerReviews?: string[];
  /**
   * Filter by latest application version eligibility label. Matches exact strings as stored.
   */
  eligibility?: EligibilityStatus[];
  versionCount?: string;
  paymentStatus?: PaymentStatus[];
}

export interface CaseFilter {
  ids?: string[];
  programId?: string;
  search?: string;
  status?: CaseStatus[];
  assigneeId?: string[];
  searchCategories?: string[];
  statusFilters?: StatusFilters;
  searchAnswersKey?: string;
  searchAnswersValue?: string;
}

export enum CaseSortColumn {
  CreatedAt = 'CreatedAt',
  UpdatedAt = 'UpdatedAt',
  SubmittedAt = 'SubmittedAt',
  DecisionReachedAt = 'DecisionReachedAt',
  PropertyAddress = 'PropertyAddress',
  Status = 'Status',
  ApplicantName = 'ApplicantName',
  Assignee = 'Assignee',
  AwardedAmount = 'AwardedAmount',
  ProgramName = 'ProgramName',
  /**
   * Sort by the latest application version's eligibility string.
   * Alphabetical sort; nulls last when ascending.
   */
  Eligibility = 'Eligibility',
}

export interface ApproveCasePaymentsInput {
  ids: string[];
  fulfillmentIds?: string[];
}

export interface OverrideCasePaymentsInput {
  ids: string[];
  payment: Pick<PaymentInput, 'fundId' | 'amount' | 'payeeType'> & { applicantTypeId?: string };
}

export interface TransitionCaseStatusesInput {
  ids: string[];
  status?: CaseStatus;
  reason?: string;
  // When assignment is not provided, that indicates autoassign should be attempted
  // If assignment is provided but assigneeId is null, that indicates unassignment
  assignment?: {
    assigneeId?: string;
  };
}

export interface ExpediteCasesInput {
  ids: string[];
  reason: string;
}

export interface CreateCaseInput {
  name: string;
  partnerId?: string;
  programId: string;
  priority?: Priority;
}

export interface AddCommentInput {
  caseId: string;
  content: string;
  parentCommentId?: string;
}

export interface AddBulkCommentInput {
  ids: string[];
  content: string;
}

export interface BulkApplyCaseTagsInput {
  ids: string[];
  tagIds: string[];
}

export interface BulkRemoveCaseTagsInput {
  ids: string[];
  tagIds: string[];
}
